import { Badge, Banner, Button, Dialog, Field, Input, InputArea, Select, Switch } from '@cloudflare/kumo';
import { FormPage, FormSection } from '../../components/ui/FormPage';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DangerButton } from '../../components/ui/DangerButton';
import { ICONS } from '../../components/ui/icons';
import { useTenant } from '../../contexts/TenantContext';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { useAppToast } from '../../hooks/useAppToast';
import { DangerConfirmModal } from '../../components/DangerConfirmModal';

interface Campaign { id: string; name: string; key: string; description: string | null; expiresAt: string | null; active: boolean; total: number; issued: number; used: number }
interface Code { id: string; code: string; issuedAt: string | null; usedAt: string | null; contact: { nome: string; telefone: string } | null }

const FILTER_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'free', label: 'Livres' },
  { value: 'issued', label: 'Entregues' },
  { value: 'used', label: 'Usados' },
];

/** Ferramentas › Cupons */
export function CuponsPage() {
  const { can } = useTenant();
  const canEdit = can('campaigns:write');
  const fetchApi = useAuthenticatedFetch();
  const toast = useAppToast();
  const [campaigns, setCampaigns] = useState<Campaign[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [codes, setCodes] = useState<{ total: number; rows: Code[] }>({ total: 0, rows: [] });
  const [filter, setFilter] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', expiresAt: '' });
  const [adding, setAdding] = useState<'paste' | 'generate' | null>(null);
  const [paste, setPaste] = useState('');
  const [gen, setGen] = useState({ count: '100', prefix: '' });
  const [redeemCode, setRedeemCode] = useState('');
  const [deleting, setDeleting] = useState<Campaign | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await fetchApi('/api/coupons');
    if (r.ok) {
      const list: Campaign[] = (await r.json()).campaigns;
      setCampaigns(list);
      setSelected((cur) => cur && list.some((c) => c.id === cur) ? cur : list[0]?.id ?? null);
    }
  }, [fetchApi]);
  useEffect(() => { load(); }, [load]);

  const loadCodes = useCallback(async () => {
    if (!selected) return;
    const r = await fetchApi(`/api/coupons/${selected}/codes?status=${filter}&q=${encodeURIComponent(q)}`);
    if (r.ok) { const d = await r.json(); setCodes({ total: d.total, rows: d.codes }); }
  }, [fetchApi, selected, filter, q]);
  useEffect(() => { loadCodes(); }, [loadCodes]);

  const call = async (url: string, init: RequestInit, ok?: (j: any) => string) => {
    setBusy(true);
    const r = await fetchApi(url, init);
    const j = await r.json().catch(() => ({}));
    setBusy(false);
    if (!r.ok) { toast.error(j.message || 'Não foi possível concluir'); return null; }
    if (ok) toast.success(ok(j));
    await load(); await loadCodes();
    return j;
  };

  const exportCsv = async () => {
    const r = await fetchApi(`/api/coupons/${selected}/export`);
    if (!r.ok) { toast.error('Não foi possível exportar'); return; }
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a');
    a.href = url; a.download = `cupons-${current?.key}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const current = campaigns?.find((c) => c.id === selected) ?? null;
  const free = current ? current.total - current.issued : 0;

  if (creating) {
    return (
      <FormPage
        backTo="/ferramentas/cupons"
        backLabel="Cupons"
        onBack={() => setCreating(false)}
        title={'Nova campanha de cupons'}
        description="Agrupa os cupons gerados para uma ação."
        submitLabel={'Criar'}
        submitting={busy}
        submitDisabled={!form.name.trim()}
        onSubmit={async () => {
          const j = await call('/api/coupons', { method: 'POST', body: JSON.stringify({ name: form.name, expiresAt: form.expiresAt ? `${form.expiresAt}T23:59:59-03:00` : null }) }, () => 'Campanha criada');
          if (j) { setCreating(false); setSelected(j.campaign.id); setAdding('generate'); }
        }}
        onCancel={() => setCreating(false)}
      >
        <FormSection title="Detalhes">


        <div className="space-y-4">
          <Field label="Nome">
            <Input onChange={(e) => setForm({ ...form, name: e.target.value })} value={form.name} placeholder="Ex.: Black Friday 10%" />
          </Field>
          <div className="flex flex-col gap-1">
            <label htmlFor="cup-exp" style={{ fontWeight: 600, fontSize: 13 }}>Válida até (opcional)</label>
            <input id="cup-exp" type="date" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} style={{ padding: '7px 12px', border: '1px solid var(--text-faint)', borderRadius: 6, fontSize: 13 }} />
          </div>
        </div>
        
        </FormSection>
      </FormPage>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 40px 0', marginBottom: 24 }}>
        <Link to="/ferramentas" className="tools-back"><ICONS.ArrowLeft size={16} /> Ferramentas</Link>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold" style={{ marginBottom: 4, fontSize: 24, fontWeight: 600 }}>Cupons</h2>
            <div style={{ color: 'var(--text-muted)' }}>
              Cada contato recebe um código único da campanha, uma vez só. Use <code>{'{{cupom:chave}}'}</code> no disparo, nas sequências ou no fluxo;
              se os códigos acabarem, a mensagem não sai sem cupom. Dê baixa aqui ou pela API (<code>POST /api/v1/coupons/redeem</code>).
            </div>
          </div>
          {canEdit && <Button variant="primary" icon={ICONS.Plus} onClick={() => { setForm({ name: '', expiresAt: '' }); setCreating(true); }}>Nova campanha</Button>}
        </div>
      </div>

      <div className="page-content space-y-6">
        {campaigns && campaigns.length === 0 && <Banner variant="default" description={<>Nenhuma campanha de cupons. Crie uma e adicione os códigos (colando os seus ou gerando aqui).</>} />}

        {canEdit && (
          <section className="brand-card flex flex-wrap items-end gap-3">
            <div style={{ flex: 1, minWidth: 240 }}>
              <Field label="Dar baixa num cupom">
                <Input onChange={(e) => setRedeemCode(e.target.value.toUpperCase())} placeholder="Código apresentado pelo cliente" value={redeemCode} />
              </Field>
            </div>
            <Button variant="secondary" disabled={!redeemCode.trim()} loading={busy} onClick={async () => {
              const j = await call('/api/coupons/redeem', { method: 'POST', body: JSON.stringify({ code: redeemCode }) }, (r) => `Cupom de ${r.coupon.contact?.nome ?? 'contato'} usado`);
              if (j) setRedeemCode('');
            }}>Dar baixa</Button>
          </section>
        )}

        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
          {campaigns?.map((c) => {
            const pct = c.total ? Math.round((c.issued / c.total) * 100) : 0;
            return (
              <button key={c.id} type="button" onClick={() => setSelected(c.id)} className="brand-card text-left"
                style={{ cursor: 'pointer', outline: c.id === selected ? '2px solid var(--brand-text)' : undefined }}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold">{c.name}</div>
                  {!c.active ? <Badge variant="neutral">Pausada</Badge> : c.expiresAt && new Date(c.expiresAt) < new Date() ? <Badge variant="red">Vencida</Badge> : c.total - c.issued <= 0 ? <Badge variant="orange">Esgotada</Badge> : <Badge variant="green">Ativa</Badge>}
                </div>
                <div className="text-xs !text-[var(--text-muted)]"><code>{`{{cupom:${c.key}}}`}</code>{c.expiresAt ? ` · até ${new Date(c.expiresAt).toLocaleDateString('pt-BR')}` : ''}</div>
                <div className="billing-meter" style={{ marginTop: 8 }} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><div className="billing-meter__fill" style={{ width: `${pct}%` }} /></div>
                <div className="text-xs !text-[var(--text-muted)]" style={{ marginTop: 4 }}>{c.issued} de {c.total} entregues · {c.used} usados</div>
              </button>
            );
          })}
        </div>

        {current && (
          <section className="brand-card space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h4 className="text-base font-semibold">{current.name} · {free.toLocaleString('pt-BR')} livres</h4>
              <div className="flex flex-wrap items-center gap-2">
                {canEdit && (
                  <label className="flex items-center gap-2" style={{ fontSize: 13 }}>
                    <Switch onCheckedChange={(checked: boolean) => call(`/api/coupons/${current.id}`, { method: 'PATCH', body: JSON.stringify({ active: checked }) }, () => (checked ? 'Campanha ativada' : 'Campanha pausada'))} aria-label="Campanha ativa" checked={current.active} />
                    Entregando
                  </label>
                )}
                {canEdit && <Button variant="secondary" onClick={() => setAdding('paste')}>Colar códigos</Button>}
                {canEdit && <Button variant="secondary" onClick={() => setAdding('generate')}>Gerar códigos</Button>}
                <Button variant="secondary" icon={ICONS.Download} onClick={exportCsv}>Exportar</Button>
                {canEdit && <IconDelete onClick={() => setDeleting(current)} />}
              </div>
            </div>
            {free <= 0 && current.total > 0 && <Banner variant="alert" description={<>Códigos esgotados: mensagens com <code>{`{{cupom:${current.key}}}`}</code> vão falhar até você adicionar mais.</>} />}
            <div className="flex flex-wrap items-center gap-3">
              <div style={{ width: 180 }}>
                <Select value={(FILTER_OPTIONS.find((o) => o.value === filter) ?? null)?.value ?? null} items={FILTER_OPTIONS} onValueChange={(value: string | null) => setFilter(value ?? '')} aria-label="Situação" />
              </div>
              <div style={{ width: 260 }}>
                <Field label="">
                  <Input onChange={(e) => setQ(e.target.value)} aria-label="Buscar" placeholder="Código, nome ou telefone" value={q} />
                </Field>
              </div>
              <div className="text-xs !text-[var(--text-muted)]">{codes.total.toLocaleString('pt-BR')} códigos</div>
            </div>
            <div id="cupons">
              {codes.rows.map((c) => (
                <div key={c.id} className="flex items-center justify-between gap-3" style={{ borderTop: '1px solid var(--border)', padding: '6px 0', fontSize: 13 }}>
                  <code>{c.code}</code>
                  <span style={{ color: 'var(--text-muted)' }}>
                    {c.contact ? `${c.contact.nome} (${c.contact.telefone}) · entregue ${c.issuedAt ? new Date(c.issuedAt).toLocaleString('pt-BR') : ''}` : 'livre'}
                    {c.usedAt ? ` · usado ${new Date(c.usedAt).toLocaleString('pt-BR')}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>



      <Dialog.Root open={!!adding} onOpenChange={(o: boolean) => { if (!o) (() => { if (adding) setAdding(null); })(); }}>
<Dialog size="sm" className="p-6">
<Dialog.Title className="mb-4 text-lg font-semibold">{<h3 className="text-lg font-semibold">{adding === 'paste' ? 'Colar códigos' : 'Gerar códigos'}</h3>}</Dialog.Title>

        {adding === 'paste' ? (
          <div className="space-y-4">
            <InputArea label="Um código por linha (ou separados por vírgula)" onChange={(e) => setPaste(e.target.value)} rows={8} value={paste} />
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setAdding(null)}>Cancelar</Button>
              <Button variant="primary" disabled={!paste.trim()} loading={busy} onClick={async () => {
                const j = await call(`/api/coupons/${selected}/codes`, { method: 'POST', body: JSON.stringify({ codes: paste }) }, (r) => `${r.added} códigos adicionados${r.duplicates ? ` (${r.duplicates} já existiam)` : ''}`);
                if (j) { setPaste(''); setAdding(null); }
              }}>Adicionar</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 grid-cols-2">
              <Field label="Quantidade">
                <Input onChange={(e) => setGen({ ...gen, count: e.target.value })} type="number" value={gen.count} />
              </Field>
              <Field label="Prefixo (opcional)">
                <Input onChange={(e) => setGen({ ...gen, prefix: e.target.value.toUpperCase() })} value={gen.prefix} placeholder="BF-" />
              </Field>
            </div>
            <div className="text-xs !text-[var(--text-muted)]">Códigos de 8 caracteres sem letras que confundem (0/O, 1/I).</div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setAdding(null)}>Cancelar</Button>
              <Button variant="primary" loading={busy} onClick={async () => {
                const j = await call(`/api/coupons/${selected}/codes`, { method: 'POST', body: JSON.stringify({ generate: { count: Number(gen.count), prefix: gen.prefix } }) }, (r) => `${r.added} códigos gerados`);
                if (j) setAdding(null);
              }}>Gerar</Button>
            </div>
          </div>
        )}
      
</Dialog>
</Dialog.Root>

      <DangerConfirmModal open={!!deleting} title="Excluir campanha de cupons" confirmText="Excluir" objectName={deleting?.name}
        onCancel={() => setDeleting(null)} onConfirm={async () => { if (deleting) await call(`/api/coupons/${deleting.id}`, { method: 'DELETE' }, () => 'Campanha excluída'); setDeleting(null); }}>
        A campanha <strong>{deleting?.name}</strong> e os {deleting?.total ?? 0} códigos (inclusive o histórico de quem recebeu) são apagados. Mensagens com
        <code>{` {{cupom:${deleting?.key}}}`}</code> passam a falhar. Não dá para desfazer.
      </DangerConfirmModal>
    </>
  );
}

function IconDelete({ onClick }: { onClick: () => void }) {
  return <DangerButton variant="dangerOutline" icon={ICONS.Trash}  onClick={onClick}>Excluir</DangerButton>;
}
